/**
 * OrderTracker — "where is my order": Received → Preparing → Ready.
 *
 * Shown on the checkout confirmation and as a banner on the order screen for
 * the device's most recent order. Driven by useOrderTracking, so it reflects
 * what the kitchen is actually doing (KDS) as well as the Square order state.
 */
import React from 'react';
import useOrderTracking from './useOrderTracking';
import { TRACK_STEPS, stageIndex, pickupLabel } from './orderFlow';

const HEADLINE = {
  awaiting_payment: 'Waiting for payment',
  received: 'Order received',
  preparing: 'Being prepared',
  ready: 'Ready for pickup!',
  picked_up: 'Picked up — enjoy!',
  canceled: 'Order canceled',
};

export default function OrderTracker({ orderId, compact = false, onChange }) {
  const { status, error } = useOrderTracking(orderId, { onChange });
  const stage = status?.stage || 'received';
  const at = stageIndex(stage);
  const canceled = stage === 'canceled';
  const detail = canceled
    ? 'This order was canceled. Call us if that\'s unexpected.'
    : stage === 'picked_up' ? 'Thanks for ordering with us.'
    : stage === 'awaiting_payment' ? 'We\'ll start cooking as soon as the payment goes through.'
    : TRACK_STEPS[Math.min(at, TRACK_STEPS.length - 1)].detail;

  return (
    <div className={`otk${compact ? ' otk--compact' : ''}${canceled ? ' otk--off' : ''}`} aria-live="polite">
      <div className="otk__head">
        <strong className="otk__headline">{status ? HEADLINE[stage] || HEADLINE.received : 'Checking your order…'}</strong>
        {status?.code && <span className="otk__code">#{status.code}</span>}
      </div>
      {!canceled && (
        <ol className="otk__steps">
          {TRACK_STEPS.map((s, i) => (
            <li key={s.stage} className={`otk__step${i <= at && status ? ' otk__step--on' : ''}${i === at && status ? ' otk__step--now' : ''}`}>
              <span className="otk__dot" aria-hidden="true">{i < at ? '✓' : i + 1}</span>
              <span className="otk__label">{s.label}</span>
            </li>
          ))}
        </ol>
      )}
      {!compact && (
        <p className="otk__detail">
          {detail}
          {status?.pickupAt && at < 2 && !canceled && <> Estimated pickup <strong>{pickupLabel(status.pickupAt)}</strong>.</>}
        </p>
      )}
      {error && !status && <p className="otk__detail">We'll keep this updated — {error.toLowerCase()}.</p>}
    </div>
  );
}
